import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { ERM, Priorities } from '../../../../Constants/Consts';
import { splitOnCapitals } from '../../../../Helpers/Functions';
import { RootState } from '../../../../store/store';
import { createDetailsSchema, PunchListDetailsType } from '../../../../types/PunchList/PunchListSchema';
import useCheckList from "../../../../Helpers/CustomHooks/useStoredCheckListObject";

import SectionHeader from "../../../../components/CoreComponents/Details/SectionHeader";
import FormDDL from '../../../../components/CoreComponents/ReactFormComponents/FormDDL';
import FormInput from '../../../../components/CoreComponents/ReactFormComponents/FormInput';
import FormYesNo from '../../../../components/CoreComponents/ReactFormComponents/FormYesNo';
import FormMultiSelect from '../../../../components/CoreComponents/ReactFormComponents/FormMultiSelect';
import { addDetails } from '../../../../store/PunchList';
import useTechnologyOptions from '../../../../Helpers/CustomHooks/useTechnologyOptions';
import useIssuesOptions from '../../../../Helpers/CustomHooks/useIssuesOptions';
import useCategoryOptions from '../../../../Helpers/CustomHooks/useCategoryOptions';

export type ErmOptionsType = {
    label: string;
    value: string;
}

type AreaType = 'Sequence' | 'CA' | 'CC' | 'Text';

const getAreaTypeMaxLength = (areaType: AreaType) => {
    if (areaType === 'Sequence') return 4;
    if (areaType === 'Text') return 32;
    return 3;
};

const parseSequenceControlArea = (value?: string) => {
    if (!value) {
        return { areaType: 'Sequence' as AreaType, number: '' };
    }
    const trimmed = String(value).trim();
    // The area type is derived from the first two letters of the control area/cabinet
    // value (used for electrical installation, electrical pre-com and controls
    // engineering RAD checklists): CA => Control Area, CC => Control Cabinet.
    // Handles legacy "CA+"/"CC+" separators as well as current formats such as
    // "CA121", "CA-121" or "CA 121" regardless of the number of digits.
    const controlAreaMatch = trimmed.match(/^(CA|CC)[+\-\s]?(\d.*)$/i);
    if (controlAreaMatch) {
        return {
            areaType: controlAreaMatch[1].toUpperCase() as AreaType,
            number: controlAreaMatch[2],
        };
    }
    if (/^\d{1,4}$/.test(trimmed)) {
        return { areaType: 'Sequence' as AreaType, number: trimmed };
    }
    return { areaType: 'Text' as AreaType, number: trimmed };
};

const formatSequenceControlArea = (areaType: AreaType, value?: string) => {
    if (!value) {
        return value;
    }
    if (areaType === 'CA') {
        return `CA${value}`;
    }
    if (areaType === 'CC') {
        return `CC${value}`;
    }
    return value;
};

export default function Details({ next, type, id }) {
    const isCheckList = type === "CheckList"
    const [selectedAreaType, setSelectedAreaType] = useState<AreaType>('Sequence');
    const maxSequenceControlAreaLength = getAreaTypeMaxLength(selectedAreaType);
    const { register, handleSubmit, formState: { errors }, control, reset, setValue, trigger, getValues } = useForm({
        resolver: async (values, context, options) =>
            zodResolver(createDetailsSchema(selectedAreaType, {
                isLockedSequenceControlArea: isCheckList,
            }))(values, context, options),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });
    const navigate = useNavigate();
    const { allDisciplines: disciplines } = useSelector((state: RootState) => state.Discipline);
    const { SelectedUser } = useSelector((state: RootState) => state.SelectedUser);
    const { selectedDD01: selectedSequenceNumber, selectedDD02: selectedUnitNumber, selectedInspectionTask, selectedPath, selectedModelNumber } = useSelector((state: RootState) => state.SequenceObject);
    const { checkListObject } = useCheckList({ path: selectedPath });
    const {
        Category, DueDate, AssignedDisciplineId, AssignedRootCauseId, AssignedRootCauseText,
        Priority, Issue, DoesPunchListRequireReinspection, DrawingHash, Technology, CreatorDisciplineId, CreatorRootCauseId, CreatorRootCauseText,
        ProjectNumber, ErmRelevant, ErmType, IssueDescription, SequenceControlArea, UnitDevice, InspectionTask, CtUuid, ModelNumber
    } = useSelector((state: RootState) => state.PunchList.PunchListObject) || {};

    const Details = {
        Category, DueDate, DrawingHash, ProjectNumber, AssignedDisciplineId, AssignedRootCauseId, AssignedRootCauseText,
        Priority, Issue, DoesPunchListRequireReinspection, Technology, CreatorDisciplineId, CreatorRootCauseId, CreatorRootCauseText,
        ErmRelevant, ErmType, IssueDescription, SequenceControlArea, UnitDevice, InspectionTask, CtUuid, ModelNumber
    };
    const dispatch = useDispatch();
    const areaTypeOptions = [
        { text: 'Sequence', value: 'Sequence' },
        { text: 'Control Area', value: 'CA' },
        { text: 'Control Cabinet', value: 'CC' },
        { text: 'Text/Number', value: 'Text' },
    ];

    const handleAreaTypeChange = (value: string) => {
        if (isCheckList) {
            return;
        }
        const nextAreaType = (value || 'Sequence') as AreaType;
        const nextMaxLength = getAreaTypeMaxLength(nextAreaType);
        const currentValue = String(getValues('SequenceControlArea') ?? '');
        setSelectedAreaType(nextAreaType);
        setValue('AreaType', nextAreaType);
        if (currentValue.length > nextMaxLength) {
            setValue('SequenceControlArea', currentValue.slice(0, nextMaxLength), { shouldValidate: true });
        } else {
            void trigger('SequenceControlArea');
        }
    };
    const onSubmit = data => {
        // Checklist/task-fail values are already Sequence/CA/CC and must not be reformatted.
        const sequenceControlAreaValue = isCheckList
            ? data.SequenceControlArea
            : formatSequenceControlArea(selectedAreaType, data.SequenceControlArea);
        dispatch(addDetails({
            ...data,
            SequenceControlArea: sequenceControlAreaValue,
        }));
        next();
    };

    useEffect(() => {
        // One source of truth: parse the value that will be shown in the input.
        const sourceValue = isCheckList
            ? String(selectedSequenceNumber ?? SequenceControlArea ?? '')
            : String(SequenceControlArea ?? '');
        const parsed = parseSequenceControlArea(sourceValue);
        const areaType = parsed.areaType;

        const dispatchItems: Partial<PunchListDetailsType> = {
            ProjectNumber: SelectedUser.projectNumber,
            DoesPunchListRequireReinspection,
            ErmRelevant,
            ErmType,
            IssueDescription,
            SequenceControlArea,
            UnitDevice,
            InspectionTask,
            ModelNumber
        };
        // Keep raw checklist value (e.g. CC400). Ad-hoc uses the number portion only.
        const sequenceControlAreaForForm = isCheckList ? sourceValue : parsed.number;

        const resetItems: Partial<PunchListDetailsType> & { AreaType: AreaType } = {
            ...Details,
            DueDate: null,
            ProjectNumber: SelectedUser.projectNumber,
            DoesPunchListRequireReinspection: DoesPunchListRequireReinspection === undefined ? true : DoesPunchListRequireReinspection,
            ErmRelevant: ErmRelevant === undefined ? false : ErmRelevant,
            ErmType,
            IssueDescription,
            SequenceControlArea: sequenceControlAreaForForm,
            UnitDevice,
            InspectionTask,
            ModelNumber,
            AreaType: areaType,
        };
        if (isCheckList) {
            dispatchItems.CtUuid = id;
            resetItems.CtUuid = id;
            resetItems.UnitDevice = String(selectedUnitNumber);
            resetItems.CreatorDisciplineId = Number(SelectedUser.disciplineId ?? "");
            resetItems.InspectionTask = selectedInspectionTask || '';
            resetItems.ModelNumber = selectedModelNumber || '';
        } else {
            dispatchItems.CreatorDisciplineId = SelectedUser.disciplineId;
            resetItems.CreatorDisciplineId = SelectedUser.disciplineId;
        }

        setSelectedAreaType(areaType);
        dispatch(addDetails(dispatchItems));
        // AreaType must be inside reset, or the dropdown falls back to Sequence.
        reset(resetItems);
    }, [type, id, selectedSequenceNumber, selectedUnitNumber, selectedInspectionTask, checkListObject, selectedModelNumber]);

    const watchErmRelevant = useWatch({ control, name: "ErmRelevant" });
    useEffect(() => {
        if (watchErmRelevant !== undefined) dispatch(addDetails({ ErmRelevant: watchErmRelevant }));
    }, [watchErmRelevant]);

    const ermOptions: ErmOptionsType[] = Object.keys(ERM).map(p => ({ label: p, value: ERM[p] }));
    const storedErmTypeValues = (ErmType ?? '')?.replaceAll(' ', '')?.split(',').filter(Boolean);
    const storedErmTypeOptions = storedErmTypeValues?.map(str => ermOptions.find(e => e.value == str));
    const [selectedErmType, setSelectedErmType] = useState(storedErmTypeOptions ?? []);

    useEffect(() => {
        const ermTypeValue = selectedErmType.map(option => option?.value).join(',');
        dispatch(addDetails({ ErmType: ermTypeValue }));
        setValue("ErmType", ermTypeValue);
    }, [selectedErmType]);

    const watchDiscipline = useWatch({ control, name: "AssignedDisciplineId" });
    const watchTechnology = useWatch({ control, name: "Technology" });
    useEffect(() => {
        const currentValue = watchTechnology ?? Technology
        dispatch(addDetails({ Technology: currentValue }));
        setValue("Technology", currentValue);
    }, [watchTechnology]);
    useEffect(() => {
        const currentValue = watchDiscipline ?? AssignedDisciplineId
        dispatch(addDetails({ AssignedDisciplineId: currentValue }));
        setValue("AssignedDisciplineId", currentValue);
    }, [watchDiscipline]);
    const watchPriority = useWatch({ control, name: "Priority" });
    useEffect(() => {
        const dateString = mapPriorityToDate(watchPriority ?? Details.Priority);
        dispatch(addDetails({ DueDate: dateString }));
        setValue("DueDate", dateString);
    }, [watchPriority]);

    const mapPriorityToDate = (priority) => {
        // Regardless of priority (High/Medium/Low), the due date is set to 2 days
        // after the punch list is created to track the 48-hour metric rule.
        let dateBasedOnPriority = new Date();
        switch (priority) {
            case Priorities.High:
            case Priorities.Medium:
            case Priorities.Low:
                dateBasedOnPriority.setDate(dateBasedOnPriority.getDate() + 2);
                break;
            default:
                dateBasedOnPriority = null;
                break;
        }
        return dateBasedOnPriority ? dateBasedOnPriority.toISOString().split('T')[0] : null;
    };

    const technologyOptions = useTechnologyOptions(AssignedDisciplineId)
    const issuesOptions = useIssuesOptions(AssignedDisciplineId)
    const categoryOptions = useCategoryOptions(AssignedDisciplineId, Technology)

    const isCategoryDisabled = categoryOptions.length === 0 && !Technology ? true : false
    const isIssueDisabled = (issuesOptions.length === 0 && !AssignedDisciplineId) ? true : false

    const areaTypeFieldConfig = {
        Sequence: {
            label: 'Sequence',
            placeholder: 'Add Sequence number',
        },
        CA: {
            label: 'Control Area',
            placeholder: 'Add Control Area number',
        },
        CC: {
            label: 'Control Cabinet',
            placeholder: 'Add Control Cabinet number',
        },
        Text: {
            label: 'Text/Number',
            placeholder: 'Add Text/Number',
        },
    } as const;
    const { label: sequenceControlAreaLabel, placeholder: sequenceControlAreaPlaceholder } =
        areaTypeFieldConfig[selectedAreaType] ?? areaTypeFieldConfig.Sequence;
    // console.log(errors);

    return (
        <div className='max-w-screen-lg' data-testid="details-form-container">
            <div className='mt-8 mb-8' data-testid="section-header-create-punch-list">
                <SectionHeader title='Create Punch List' />
            </div>
            <div data-testid="details-form-form-container">
                <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
                        e.preventDefault();
                    }
                }} data-testid="details-form-form-elements">
                    <FormInput
                        label={splitOnCapitals("ProjectNumber")}
                        errors={errors}
                        inputKey="ProjectNumber"
                        registeredProps={register("ProjectNumber")}
                        disabled={true}
                        required={true}
                        placeholder={''}
                        defaultValue={ProjectNumber}
                        dataTestId="punchlist-form-project-number"
                    />
                    <FormDDL
                        options={disciplines?.map(d => ({ text: String(d.disciplineName), value: d.disciplineId }))}
                        control={control}
                        errors={errors}
                        defaultValue={SelectedUser.disciplineId}
                        inputKey="CreatorDisciplineId"
                        label={splitOnCapitals("CreatorDisciplineId")}
                        name={"CreatorDisciplineId"}
                        required={true}
                        disabled={true}
                        dataTestId="punchlist-form-creator-discipline-id"
                    />
                    <FormDDL
                        options={disciplines?.map(d => ({ text: String(d.disciplineName), value: d.disciplineId }))}
                        control={control}
                        errors={errors}
                        defaultValue={AssignedDisciplineId}
                        inputKey="AssignedDisciplineId"
                        label={splitOnCapitals("AssignedDisciplineId")}
                        name={"AssignedDisciplineId"}
                        required={true}
                        dataTestId="punchlist-form-assigned-discipline-id"
                    />
                    <FormDDL
                        options={technologyOptions}
                        control={control}
                        errors={errors}
                        // defaultValue={Category}
                        inputKey="Technology"
                        label="Technology"
                        name="Technology"
                        required={true}
                        dataTestId="punchlist-form-technology"
                    />
                    <FormDDL
                        options={categoryOptions}
                        control={control}
                        errors={errors}
                        defaultValue={Category}
                        inputKey="Category"
                        label="Task"
                        name="Category"
                        required={true}
                        disabled={isCategoryDisabled}
                        dataTestId="punchlist-form-category"
                    />
                    <FormDDL
                        options={issuesOptions}
                        control={control}
                        errors={errors}
                        defaultValue={Issue}
                        inputKey="Issue"
                        label="Issue"
                        name="Issue"
                        required={true}
                        disabled={isIssueDisabled}
                        dataTestId="punchlist-form-issue"
                    />
                    <FormInput
                        errors={errors}
                        inputKey="DrawingHash"
                        registeredProps={register("DrawingHash")}
                        label={splitOnCapitals("Drawing #")}
                        placeholder={''}
                        required={false}
                        defaultValue={DrawingHash}
                        dataTestId="punchlist-form-drawing-hash"
                    />
                    <FormInput
                        errors={errors}
                        inputKey="ModelNumber"
                        registeredProps={register("ModelNumber")}
                        label="Model Number"
                        placeholder='Enter Model Number'
                        required={true}
                        defaultValue={isCheckList ? (selectedModelNumber || '') : (ModelNumber || '')}
                        disabled={isCheckList}
                        dataTestId="punchlist-form-model-number"
                    />
                    {isCheckList && (
                        <FormInput
                            label="Inspection Task"
                            errors={errors}
                            inputKey="InspectionTask"
                            registeredProps={register("InspectionTask")}
                            disabled={true}
                            required={true}
                            placeholder={''}
                            defaultValue={selectedInspectionTask || ''}
                            isTextArea={true}
                            dataTestId="punchlist-form-inspectiontask"
                        />
                    )}
                    <FormDDL
                        key={`area-type-${selectedAreaType}-${isCheckList ? selectedSequenceNumber : 'adhoc'}`}
                        options={areaTypeOptions}
                        control={control}
                        errors={errors}
                        defaultValue={selectedAreaType}
                        inputKey="AreaType"
                        label="Area Type"
                        name="AreaType"
                        disabled={isCheckList}
                        onChangeEvent={handleAreaTypeChange}
                        dataTestId="punchlist-form-area-type"
                    />
                    <FormInput
                        key={`sequence-control-area-${selectedAreaType}-${isCheckList ? selectedSequenceNumber : 'adhoc'}`}
                        errors={errors}
                        inputKey="SequenceControlArea"
                        registeredProps={register("SequenceControlArea")}
                        label={sequenceControlAreaLabel}
                        disabled={isCheckList}
                        placeholder={sequenceControlAreaPlaceholder}
                        defaultValue={
                            isCheckList
                                ? String(selectedSequenceNumber ?? SequenceControlArea ?? '')
                                : parseSequenceControlArea(SequenceControlArea).number
                        }
                        maxLength={isCheckList ? undefined : maxSequenceControlAreaLength}
                        dataTestId="punchlist-form-sequence-control-area"
                    />
                    <FormInput
                        errors={errors}
                        inputKey="UnitDevice"
                        registeredProps={register("UnitDevice")}
                        label="Unit or Device"
                        disabled={isCheckList}
                        placeholder={''}
                        defaultValue={UnitDevice}
                        dataTestId="punchlist-form-unit-device"
                    />
                    <FormInput
                        errors={errors}
                        inputKey="IssueDescription"
                        registeredProps={register("IssueDescription")}
                        label={splitOnCapitals("IssueDescription")}
                        placeholder={''}
                        required={true}
                        defaultValue={IssueDescription}
                        isTextArea={true}
                        dataTestId="punchlist-form-issue-description"
                    />
                    <FormYesNo
                        required={false}
                        defaultValue={ErmRelevant}
                        register={register}
                        errors={errors}
                        control={control}
                        name="ErmRelevant"
                        inputKey="ErmRelevant"
                        label="Does This Punch List Add Risk To The Project?"
                        dataTestId="punchlist-form-erm-relevant"
                    />
                    <div className='mt-2' hidden={!ErmRelevant}>
                        <FormMultiSelect
                            required={true}
                            options={ermOptions}
                            control={control}
                            errors={errors}
                            defaultValue={selectedErmType}
                            inputKey={"ErmType"}
                            label={"ERM type"}
                            name={"selectedErmType"}
                            dataTestId="punchlist-form-erm-type"
                            onChangeEvent={(selectedOptions: ErmOptionsType[]) => {
                                const values = selectedOptions
                                setSelectedErmType(values);
                            }}
                        />
                    </div>
                    <FormDDL
                        required={true}
                        requiredText={'(Will automatically assign Due Date)'}
                        options={Object.keys(Priorities).map(p => ({ text: p, value: Priorities[p] }))}
                        control={control}
                        errors={errors}
                        defaultValue={Priority}
                        inputKey="Priority"
                        label="Priority"
                        name="Priority"
                        dataTestId="punchlist-form-priority"
                    />
                    <FormInput
                        errors={errors}
                        type='Date'
                        inputKey="DueDate"
                        registeredProps={register("DueDate", {
                            valueAsDate: false
                        })}
                        label={splitOnCapitals("DueDate")}
                        required={true}
                        requiredText='(Can update if desired)'
                        placeholder={''}
                        defaultValue={DueDate}
                        dataTestId="punchlist-form-due-date"
                    />
                    <FormYesNo
                        required={false}
                        defaultValue={DoesPunchListRequireReinspection}
                        register={register}
                        errors={errors}
                        control={control}
                        name="DoesPunchListRequireReinspection"
                        inputKey="DoesPunchListRequireReinspection"
                        label="Requires Re-Inspection?"
                        dataTestId="punchlist-form-does-punch-list-require-re-inspection"
                    />
                    <div className='flex mt-8 mb-8 space-x-4'>
                        <button className='bg-gray-400 px-4 py-2' data-testid="details-form-cancel-button" onClick={() => navigate(-1)}>
                            Cancel
                        </button>
                        <div className="ml-auto w-full md:w-auto">
                            <button className='save-btn px-4 py-2 w-full md:w-auto' data-testid="details-form-next-button" type='submit' >
                                Next
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
